import { useMemo, useState } from 'react';
import { analyzerAPI, extractErrorMessage } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Play, Loader2, Info, RefreshCw, Trash2 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Cell,
} from 'recharts';
import { formatCurrency, formatPercent, formatNumber } from '../lib/utils';
import { getReturnMetric } from '../lib/returnMetric';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * BLOQUE 6 · Stress Test Panel
 * ─────────────────────────────────────────────────────────────────
 * Orquesta corridas hipotéticas contra `/api/analyzer/stress-run`:
 *   · Serie estricta (concurrency=1) — el lock optimista es por scenario_id.
 *   · Cero matemática en el cliente. Cada celda y cada barra del tornado
 *     proviene de una corrida REAL del backend.
 *   · Estado VOLÁTIL (sesión) — no persiste en analysis_runs.
 *   · Dimensiones de stress (matchean variables del Engine v2.6.0):
 *       - tiie_annual_change   (G3 · drift anual de TIIE, puntos %/año)
 *       - loan_interest_rate_annual  (nivel base de la tasa)
 *       - inflation_general_pct (G4 · inflación general)
 *       - expected_overrun_pct  (G5 · sobrecosto esperado, Modelo B)
 *       - contingency_pct       (colchón de contingencia)
 */

// Dimensiones modeladas en %, con conversión transparente a fracción para
// el backend (ej. 5% → 0.05). La inflación, contingencia y overrun se guardan
// en fracción en el motor. tiie_annual_change se guarda en fracción (0.025 =
// 2.5%/año de drift). loan_interest_rate_annual también se guarda en fracción.
const DIMENSIONS = [
  {
    key: 'tiie_annual_change',
    label: 'TIIE · drift anual',
    unit: '%/año',
    help: 'Cambio anual de TIIE expresado en puntos porcentuales por año (validado en G3). Adverso = +2.5%/año significa que la tasa sube 2.5 puntos cada año.',
    step: 0.25,
    min: -5,
    max: 10,
    factor: 0.01, // % → fracción
  },
  {
    key: 'loan_interest_rate_annual',
    label: 'Tasa base anual',
    unit: '%',
    help: 'Nivel BASE de la tasa anual del crédito (no el drift). Sobre este nivel se aplica el drift de TIIE.',
    step: 0.25,
    min: 0,
    max: 30,
    factor: 0.01,
  },
  {
    key: 'inflation_general_pct',
    label: 'Inflación general',
    unit: '%',
    help: 'Inflación general anual aplicada a costos admin/supervisión (G4). NO afecta construcción ni precios.',
    step: 0.5,
    min: 0,
    max: 25,
    factor: 0.01,
  },
  {
    key: 'expected_overrun_pct',
    label: 'Sobrecosto esperado',
    unit: '%',
    help: 'Sobrecosto esperado sobre costos duros (G5 · Modelo B). Si supera la contingencia, el exceso impacta directo al costo.',
    step: 1,
    min: 0,
    max: 30,
    factor: 0.01,
  },
  {
    key: 'contingency_pct',
    label: 'Contingencia',
    unit: '%',
    help: 'Colchón de contingencia (% sobre costos duros). Absorbe el sobrecosto esperado hasta su tope.',
    step: 0.5,
    min: 0,
    max: 25,
    factor: 0.01,
  },
];

// Presets en UNIDADES DE UI (%); se convierten a fracción al enviar al backend.
const PRESETS = [
  {
    id: 'optimista',
    label: 'Optimista',
    tone: 'success',
    values: {
      tiie_annual_change: -1.5,
      inflation_general_pct: 3,
      expected_overrun_pct: 0,
      contingency_pct: 5,
    },
  },
  {
    id: 'conservador',
    label: 'Conservador',
    tone: 'info',
    values: {
      tiie_annual_change: 1.0,
      inflation_general_pct: 5,
      expected_overrun_pct: 5,
      contingency_pct: 8,
    },
  },
  {
    id: 'adverso',
    label: 'Adverso',
    tone: 'warning',
    values: {
      tiie_annual_change: 2.5,
      inflation_general_pct: 7,
      expected_overrun_pct: 12,
      contingency_pct: 10,
    },
  },
];


// Convierte un valor UI (%) a fracción para el backend, o pasa null si no hay valor.
function toEngineValue(uiVal, dim) {
  if (uiVal === '' || uiVal === null || uiVal === undefined || Number.isNaN(Number(uiVal))) return null;
  return Number(uiVal) * dim.factor;
}

// Inversa: fracción del motor → UI (%)
function toUiValue(engineVal, dim) {
  if (engineVal === null || engineVal === undefined) return '';
  return Number((engineVal / dim.factor).toFixed(3));
}

// Convierte un objeto `{key: %}` a `{key: fracción}` (whitelist)
function buildOverrides(uiValues) {
  const out = {};
  for (const dim of DIMENSIONS) {
    const v = toEngineValue(uiValues[dim.key], dim);
    if (v !== null) out[dim.key] = v;
  }
  return out;
}

function MetricCell({ value, format, comparison }) {
  let display = '—';
  if (value !== null && value !== undefined && !Number.isNaN(Number(value))) {
    if (format === 'pct') display = formatPercent(value);
    else if (format === 'cur') display = formatCurrency(value);
    else if (format === 'x') display = `${formatNumber(value, 2)}x`;
    else display = formatNumber(value, 2);
  }
  let color = 'var(--text-primary)';
  if (comparison === 'hurdle-pass') color = 'var(--success)';
  if (comparison === 'hurdle-fail') color = 'var(--danger)';
  return (
    <span className="text-xs font-display font-semibold tabular-nums" style={{ color }}>
      {display}
    </span>
  );
}

function DeltaPill({ delta, format }) {
  if (delta === null || delta === undefined || Number.isNaN(Number(delta))) return null;
  const positive = delta > 0;
  // Tolerancia de "≈0" depende del formato (mirr en pp, moic en x, currency en $)
  const negligibleThreshold = format === 'cur' ? 1 : format === 'pct' ? 0.01 : 0.005;
  const negligible = Math.abs(delta) < negligibleThreshold;
  if (negligible) {
    return (
      <span className="text-[9px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full inline-block ml-1"
        style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>
        ±0
      </span>
    );
  }
  const sign = positive ? '+' : '';
  let txt;
  if (format === 'pct') txt = `${sign}${delta.toFixed(2)}pp`;
  else if (format === 'cur') {
    const abs = Math.abs(delta);
    const m = abs >= 1e6 ? `$${(abs / 1e6).toFixed(1)}M` : `$${(abs / 1e3).toFixed(0)}K`;
    txt = `${positive ? '+' : '−'}${m}`;
  } else if (format === 'x') txt = `${sign}${delta.toFixed(2)}x`;
  else txt = `${sign}${delta.toFixed(2)}`;
  return (
    <span className="text-[9px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full inline-block ml-1"
      style={{
        background: positive ? 'var(--success-soft)' : 'var(--danger-soft)',
        color: positive ? 'var(--success)' : 'var(--danger)',
      }}>
      {txt}
    </span>
  );
}

export default function StressTestPanel({ projectId, scenarioId, baseAnalysis }) {
  // Valores de los controles (UI: en %)
  const baseUiValues = useMemo(() => {
    const a = baseAnalysis?.assumptions || {};
    const out = {};
    for (const dim of DIMENSIONS) {
      out[dim.key] = toUiValue(a[dim.key], dim);
    }
    return out;
  }, [baseAnalysis]);

  const [uiValues, setUiValues] = useState({});
  const [activePreset, setActivePreset] = useState(null);
  const [pendingRuns, setPendingRuns] = useState([]); // [{id, label, overrides}]
  const [results, setResults] = useState([]); // [{id, label, status, metrics, overrides, error}]
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: null });
  const [tornadoMetric, setTornadoMetric] = useState('mirr'); // 'mirr' | 'moic'
  const [tornadoSkipped, setTornadoSkipped] = useState([]); // SWA-139 · variables sin métrica interpretable

  // Valor efectivo (lo que el usuario ve en el input) — fusiona base con overrides
  const getEffective = (key) => {
    const v = uiValues[key];
    if (v === '' || v === undefined || v === null) return baseUiValues[key];
    return v;
  };

  const handleInputChange = (key, value) => {
    setUiValues((prev) => ({ ...prev, [key]: value }));
    setActivePreset(null);
  };

  const applyPreset = (preset) => {
    const next = { ...uiValues };
    for (const k of Object.keys(preset.values)) {
      next[k] = preset.values[k];
    }
    setUiValues(next);
    setActivePreset(preset.id);
  };

  const resetToBase = () => {
    setUiValues({});
    setActivePreset(null);
  };

  // Construye la batería de corridas: 1 corrida CUSTOM + 1 por preset + 1 base.
  // Si el usuario tocó controles, también se incluye CUSTOM. Si no, solo los presets.
  const buildBattery = () => {
    const battery = [];
    // BASE: sin overrides (corrida "tal cual está el escenario")
    battery.push({ id: 'base', label: 'Base', overrides: {} });
    // PRESETS
    for (const p of PRESETS) {
      battery.push({
        id: `preset-${p.id}`,
        label: p.label,
        overrides: buildOverrides({ ...baseUiValues, ...p.values }),
        tone: p.tone,
      });
    }
    // CUSTOM (solo si el usuario tocó valores y no coincide con un preset activo)
    const userTouched = Object.values(uiValues).some(v => v !== '' && v !== undefined && v !== null);
    if (userTouched) {
      battery.push({
        id: 'custom',
        label: activePreset ? 'Custom' : 'Custom',
        overrides: buildOverrides({ ...baseUiValues, ...uiValues }),
        tone: 'critical',
      });
    }
    return battery;
  };

  // Corre las corridas EN SERIE (concurrency=1). Cada corrida actualiza el
  // estado de progreso y agrega/actualiza la fila correspondiente.
  const runBattery = async () => {
    if (!projectId || !scenarioId) {
      toast.error('Selecciona proyecto y escenario');
      return;
    }
    const battery = buildBattery();
    setPendingRuns(battery);
    setResults(battery.map(b => ({ ...b, status: 'pending', metrics: null })));
    setRunning(true);
    setProgress({ done: 0, total: battery.length, current: battery[0]?.label });

    for (let i = 0; i < battery.length; i++) {
      const r = battery[i];
      setProgress({ done: i, total: battery.length, current: r.label });
      try {
        const res = await analyzerAPI.stressRun({
          scenario_id: scenarioId,
          project_id: projectId,
          overrides: r.overrides,
          label: r.label,
        });
        setResults((prev) => prev.map(p => p.id === r.id ? {
          ...p,
          status: 'ok',
          metrics: res.data.metrics,
          truncation_report: res.data.truncation_report,
          overrun_report: res.data.overrun_report,
          applied_overrides: res.data.applied_overrides,
          duration_ms: res.data.duration_ms,
        } : p));
      } catch (err) {
        setResults((prev) => prev.map(p => p.id === r.id ? {
          ...p,
          status: 'error',
          error: extractErrorMessage(err, 'Error en stress run'),
        } : p));
      }
    }

    setProgress({ done: battery.length, total: battery.length, current: null });
    setRunning(false);
    toast.success(`Stress test ejecutado · ${battery.length} corridas`);
  };

  // Construye el dataset del tornado.
  // Para cada dimensión: corre +preset Adverso y +preset Optimista (ya están en results).
  // El delta se mide siempre vs Base (no vs preset adyacente).
  // El usuario alterna entre MIRR/MOIC.
  // Tornado real "por variable": una variable a la vez, manteniendo las demás
  // iguales a la base. Lo armamos como una segunda batería bajo demanda.
  const [varTornado, setVarTornado] = useState(null); // [{variable, low, high, label}]
  const [runningTornado, setRunningTornado] = useState(false);

  const runVariableTornado = async () => {
    if (!projectId || !scenarioId) {
      toast.error('Selecciona proyecto y escenario');
      return;
    }
    setRunningTornado(true);
    // Para cada dimensión: corre OPT (preset optimista solo en esa dim) y ADV (preset adverso solo en esa dim).
    // Base ya está corrida si results.base existe; si no, la corre primero.
    let baseMetrics = results.find(r => r.id === 'base' && r.status === 'ok')?.metrics;
    if (!baseMetrics) {
      try {
        const baseRes = await analyzerAPI.stressRun({
          scenario_id: scenarioId, project_id: projectId, overrides: {}, label: 'Base',
        });
        baseMetrics = baseRes.data.metrics;
      } catch (err) {
        toast.error(extractErrorMessage(err, 'Error corriendo Base'));
        setRunningTornado(false);
        return;
      }
    }
    // SWA-139 · nunca caer en irr_annual: si el retorno no es MIRR, el punto no entra al Tornado.
    const baseRM = getReturnMetric(baseMetrics);
    const baseVal = tornadoMetric === 'mirr'
      ? (baseRM.mirrUsable ? baseRM.mirrValue : null)
      : baseMetrics?.moic;
    const skipped = [];

    const out = [];
    for (const dim of DIMENSIONS.filter(d => d.key !== 'loan_interest_rate_annual')) {
      const optVal = PRESETS.find(p => p.id === 'optimista').values[dim.key];
      const advVal = PRESETS.find(p => p.id === 'adverso').values[dim.key];
      const optOverrides = optVal !== undefined
        ? { [dim.key]: optVal * dim.factor } : {};
      const advOverrides = advVal !== undefined
        ? { [dim.key]: advVal * dim.factor } : {};
      let optMetric = null, advMetric = null;
      try {
        const r1 = await analyzerAPI.stressRun({
          scenario_id: scenarioId, project_id: projectId, overrides: optOverrides, label: `${dim.label} · Optimista`,
        });
        const r1RM = getReturnMetric(r1.data.metrics);
        optMetric = tornadoMetric === 'mirr'
          ? (r1RM.mirrUsable ? r1RM.mirrValue : null)
          : r1.data.metrics?.moic;
      } catch { /* swallow */ }
      try {
        const r2 = await analyzerAPI.stressRun({
          scenario_id: scenarioId, project_id: projectId, overrides: advOverrides, label: `${dim.label} · Adverso`,
        });
        const r2RM = getReturnMetric(r2.data.metrics);
        advMetric = tornadoMetric === 'mirr'
          ? (r2RM.mirrUsable ? r2RM.mirrValue : null)
          : r2.data.metrics?.moic;
      } catch { /* swallow */ }
      if (baseVal == null || optMetric == null || advMetric == null) {
        skipped.push(dim.label);
      }
      out.push({
        variable: dim.label,
        low: optMetric != null && baseVal != null ? optMetric - baseVal : 0,
        high: advMetric != null && baseVal != null ? advMetric - baseVal : 0,
        baseVal,
        incomplete: baseVal == null || optMetric == null || advMetric == null,
      });
    }
    out.sort((a, b) => (Math.abs(b.high) + Math.abs(b.low)) - (Math.abs(a.high) + Math.abs(a.low)));
    setVarTornado(out);
    setTornadoSkipped(skipped);
    setRunningTornado(false);
    if (skipped.length > 0 && tornadoMetric === 'mirr') {
      toast.warning(`Tornado incompleto: ${skipped.length} variable(s) sin MIRR interpretable`);
    } else {
      toast.success('Tornado por variable listo');
    }
  };

  const clearAll = () => {
    setResults([]);
    setPendingRuns([]);
    setVarTornado(null);
    setProgress({ done: 0, total: 0, current: null });
  };

  const baseRow = results.find(r => r.id === 'base' && r.status === 'ok');
  const hurdleRate = baseRow?.metrics?.hurdle_rate ?? baseAnalysis?.metrics?.hurdle_rate ?? null; // SWA-353

  return (
    <div className="space-y-5" data-testid="stress-test-panel">
      {/* Header */}
      <Card className="rounded-xl border-border/50 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div>
              <h3 className="text-base font-display font-semibold">Stress Test</h3>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                Corridas hipotéticas en serie contra el motor v{baseAnalysis?.engine_version || '2.6.0'}.
                El frontend NO recalcula nada — cada celda viene de una corrida real del backend.
                Estas corridas son volátiles y NO se persisten en el historial.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {results.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  disabled={running || runningTornado}
                  data-testid="stress-clear-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Limpiar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Presets + Controls */}
      <Card className="rounded-xl border-border/50 shadow-sm">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Presets</p>
            <div className="flex items-center gap-1.5">
              {PRESETS.map(p => (
                <Button
                  key={p.id}
                  variant={activePreset === p.id ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => applyPreset(p)}
                  disabled={running || runningTornado}
                  data-testid={`preset-${p.id}-btn`}
                >
                  {p.label}
                </Button>
              ))}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={resetToBase}
                disabled={running || runningTornado}
                data-testid="preset-reset-btn"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Reset
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {DIMENSIONS.map(dim => (
              <div key={dim.key} className="space-y-1">
                <div className="flex items-center gap-1.5">
                  <Label className="text-[11px] font-medium text-muted-foreground">{dim.label}</Label>
                  <TooltipProvider delayDuration={200}>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-muted-foreground/40 hover:text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[280px] p-2.5">
                        <p className="text-[11px] leading-relaxed">{dim.help}</p>
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                  <span className="text-[10px] text-muted-foreground ml-auto">Base: {baseUiValues[dim.key] !== '' ? `${baseUiValues[dim.key]}${dim.unit}` : '—'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    step={dim.step}
                    min={dim.min}
                    max={dim.max}
                    value={getEffective(dim.key) ?? ''}
                    onChange={(e) => handleInputChange(dim.key, e.target.value)}
                    disabled={running || runningTornado}
                    className="h-8 text-xs tabular-nums"
                    data-testid={`stress-input-${dim.key}`}
                  />
                  <span className="text-[10px] text-muted-foreground w-10 text-right">{dim.unit}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-border/30 gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5" />
              <span>Corridas en serie · concurrency 1 (respeta el lock optimista del escenario)</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={runBattery}
                disabled={running || runningTornado || !projectId || !scenarioId}
                size="sm"
                data-testid="stress-run-battery-btn"
              >
                {running ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Play className="h-4 w-4 mr-1.5" />}
                Correr stress
              </Button>
            </div>
          </div>

          {/* Progress */}
          <AnimatePresence>
            {running && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-2 p-3 rounded-lg" style={{ background: 'var(--bg-hover)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-medium text-muted-foreground">
                      Ejecutando {progress.current || '…'}
                    </span>
                    <span className="text-[11px] tabular-nums font-semibold">
                      {progress.done}/{progress.total}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-default)' }}>
                    <div
                      className="h-full transition-all duration-300"
                      style={{
                        width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`,
                        background: 'var(--module-primary, #635BFF)',
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* Results Table */}
      {results.length > 0 && (
        <Card className="rounded-xl border-border/50 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-display font-semibold">Tabla comparativa</h4>
              {hurdleRate != null && (
                <span className="text-[10px] text-muted-foreground">
                  Hurdle MIRR ≥ {hurdleRate}%
                </span>
              )}
            </div>
            <div className="overflow-x-auto rounded-lg border border-border/40">
              <table className="w-full text-xs" data-testid="stress-results-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>
                    <th className="text-left px-3 py-2 font-semibold">Escenario</th>
                    <th className="text-right px-2 py-2 font-semibold">MIRR inv.</th>
                    <th className="text-right px-2 py-2 font-semibold">MOIC</th>
                    <th className="text-right px-2 py-2 font-semibold">VAN inv.</th>
                    <th className="text-right px-2 py-2 font-semibold">Peak Debt</th>
                    <th className="text-right px-2 py-2 font-semibold">Peak Equity</th>
                    <th className="text-right px-2 py-2 font-semibold">Utilidad</th>
                    <th className="text-center px-2 py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((row) => {
                    const m = row.metrics || {};
                    const baseM = baseRow?.metrics || {};
                    const mirrFromRow = m.mirr != null ? m.mirr : null; // engine returns mirr en % (ej 18.5)
                    const baseMirr = baseM.mirr != null ? baseM.mirr : null;
                    const isBase = row.id === 'base';
                    return (
                      <tr key={row.id} className="border-t border-border/30 hover:bg-accent/20"
                        data-testid={`stress-row-${row.id}`}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-display font-semibold">{row.label}</span>
                            {isBase && (
                              <Badge variant="outline" className="text-[9px] px-1.5 py-0">Base</Badge>
                            )}
                            {row.tone === 'warning' && (
                              <Badge variant="outline" className="text-[9px] px-1.5 py-0" style={{ borderColor: 'var(--warning-color)60', color: 'var(--warning-color)' }}>
                                Adverso
                              </Badge>
                            )}
                            {row.status === 'pending' && (
                              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                            )}
                            {row.status === 'error' && (
                              <Badge variant="outline" className="text-[9px] px-1.5 py-0" style={{ borderColor: 'var(--danger)60', color: 'var(--danger)' }}>
                                Error
                              </Badge>
                            )}
                          </div>
                          {row.truncation_report?.total_truncated > 0 && (
                            <p className="text-[9px] text-amber-700 mt-0.5">
                              ⚠ Truncamiento {formatCurrency(row.truncation_report.total_truncated)}
                            </p>
                          )}
                          {row.overrun_report?.status === 'exceeds' && (
                            <p className="text-[9px] text-amber-700 mt-0.5">
                              ⚠ Sobrecosto excede contingencia
                            </p>
                          )}
                          {row.error && (
                            <p className="text-[9px] text-red-700 mt-0.5">{row.error}</p>
                          )}
                        </td>
                        <td className="text-right px-2 py-2">
                          <MetricCell
                            value={mirrFromRow}
                            format="pct"
                            comparison={mirrFromRow != null && m.hurdle_pass != null ? (m.hurdle_pass ? 'hurdle-pass' : 'hurdle-fail') : null}
                          />
                          {!isBase && mirrFromRow != null && baseMirr != null && (
                            <div className="mt-0.5"><DeltaPill delta={mirrFromRow - baseMirr} format="pct" /></div>
                          )}
                        </td>
                        <td className="text-right px-2 py-2">
                          <MetricCell value={m.moic} format="x" />
                          {!isBase && m.moic != null && baseM.moic != null && (
                            <div className="mt-0.5"><DeltaPill delta={m.moic - baseM.moic} format="x" /></div>
                          )}
                        </td>
                        <td className="text-right px-2 py-2">
                          <MetricCell value={m.npv} format="cur" />
                          {!isBase && m.npv != null && baseM.npv != null && (
                            <div className="mt-0.5"><DeltaPill delta={m.npv - baseM.npv} format="cur" /></div>
                          )}
                        </td>
                        <td className="text-right px-2 py-2">
                          <MetricCell value={m.peak_debt} format="cur" />
                        </td>
                        <td className="text-right px-2 py-2">
                          <MetricCell value={m.peak_equity} format="cur" />
                          {!isBase && m.peak_equity != null && baseM.peak_equity != null && (
                            <div className="mt-0.5"><DeltaPill delta={m.peak_equity - baseM.peak_equity} format="cur" /></div>
                          )}
                        </td>
                        <td className="text-right px-2 py-2">
                          <MetricCell value={m.total_profit} format="cur" />
                          {!isBase && m.total_profit != null && baseM.total_profit != null && (
                            <div className="mt-0.5"><DeltaPill delta={m.total_profit - baseM.total_profit} format="cur" /></div>
                          )}
                        </td>
                        <td className="text-center px-2 py-2">
                          {row.status === 'ok' && (
                            <span className="text-[10px]" style={{ color: 'var(--success)' }}>✓</span>
                          )}
                          {row.status === 'error' && (
                            <span className="text-[10px]" style={{ color: 'var(--danger)' }}>✗</span>
                          )}
                          {row.status === 'pending' && (
                            <Loader2 className="h-3 w-3 animate-spin mx-auto text-muted-foreground" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tornado */}
      <Card className="rounded-xl border-border/50 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <h4 className="text-sm font-display font-semibold">Tornado de sensibilidad por variable</h4>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Mueve UNA dimensión a la vez (optimista vs adverso) manteniendo las demás iguales a la base.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center rounded-md overflow-hidden border border-border/50">
                <button
                  className={`px-2.5 py-1 text-[10px] font-semibold transition-colors ${tornadoMetric === 'mirr' ? '' : 'opacity-60'}`}
                  style={{ background: tornadoMetric === 'mirr' ? 'var(--module-primary)' : 'transparent', color: tornadoMetric === 'mirr' ? '#fff' : 'var(--text-primary)' }}
                  onClick={() => setTornadoMetric('mirr')}
                  data-testid="tornado-toggle-mirr"
                >
                  Δ MIRR
                </button>
                <button
                  className={`px-2.5 py-1 text-[10px] font-semibold transition-colors ${tornadoMetric === 'moic' ? '' : 'opacity-60'}`}
                  style={{ background: tornadoMetric === 'moic' ? 'var(--module-primary)' : 'transparent', color: tornadoMetric === 'moic' ? '#fff' : 'var(--text-primary)' }}
                  onClick={() => setTornadoMetric('moic')}
                  data-testid="tornado-toggle-moic"
                >
                  Δ MOIC
                </button>
              </div>
              <Button
                onClick={runVariableTornado}
                disabled={running || runningTornado || !projectId || !scenarioId}
                size="sm"
                variant="outline"
                data-testid="tornado-run-btn"
              >
                {runningTornado ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Play className="h-4 w-4 mr-1.5" />}
                Generar tornado
              </Button>
            </div>
          </div>

          {varTornado && tornadoSkipped.length > 0 && (
            <div className="mb-2 px-3 py-2 rounded-md text-[11px]" style={{ background: 'var(--warning-subtle, #FEF3C7)', color: 'var(--warning-color)' }} data-testid="tornado-skipped-notice">
              Tornado incompleto · sin {tornadoMetric === 'mirr' ? 'MIRR' : 'MOIC'} interpretable en: {tornadoSkipped.join(', ')}. Esas barras se muestran en cero; no son un efecto nulo.
            </div>
          )}

          {!varTornado && !runningTornado && (
            <div className="text-center py-10 text-xs text-muted-foreground" data-testid="tornado-empty">
              Aún sin tornado. Presiona "Generar tornado" para correr una variable a la vez.
            </div>
          )}

          {runningTornado && (
            <div className="text-center py-10 text-xs text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Generando tornado · {DIMENSIONS.filter(d => d.key !== 'loan_interest_rate_annual').length * 2} corridas en serie…
            </div>
          )}

          {varTornado && !runningTornado && (
            <div data-testid="tornado-chart">
              <ResponsiveContainer width="100%" height={Math.max(220, varTornado.length * 46)}>
                <BarChart
                  data={varTornado}
                  layout="vertical"
                  stackOffset="sign"
                  margin={{ top: 8, right: 30, left: 80, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                  <XAxis
                    type="number"
                    tickFormatter={(v) => tornadoMetric === 'mirr' ? `${v.toFixed(1)}pp` : `${v.toFixed(2)}x`}
                    tick={{ fill: '#64748B', fontSize: 11 }}
                  />
                  <YAxis dataKey="variable" type="category" tick={{ fill: '#1F2937', fontSize: 11 }} width={140} />
                  <Tooltip
                    formatter={(v, name) => {
                      const label = name === 'low' ? 'Optimista' : 'Adverso';
                      const text = tornadoMetric === 'mirr'
                        ? `${v.toFixed(2)}pp`
                        : `${v.toFixed(3)}x`;
                      return [text, label];
                    }}
                  />
                  <ReferenceLine x={0} stroke="#94A3B8" />
                  <Bar dataKey="low" stackId="a" name="Optimista" radius={[2, 2, 2, 2]}>
                    {varTornado.map((entry, i) => (
                      <Cell key={`opt-${i}`} fill={entry.low >= 0 ? '#0CBF73' : '#DF1B41'} />
                    ))}
                  </Bar>
                  <Bar dataKey="high" stackId="a" name="Adverso" radius={[2, 2, 2, 2]}>
                    {varTornado.map((entry, i) => (
                      <Cell key={`adv-${i}`} fill={entry.high >= 0 ? '#0CBF73' : '#DF1B41'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="text-[10px] text-muted-foreground mt-2 text-center">
                Δ medido contra la corrida Base. Verde = mejora, Rojo = deterioro.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
